/**
 * Results Card - Zeigt alle Ergebnisse der Kalkulation
 */

import type { CalculatorResult } from '@/types/price-calculator';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ResultsCardProps {
  result: CalculatorResult;
}

function StatBox({
  label,
  value,
  suffix = '€',
  highlight = false,
  negative = false,
}: {
  label: string;
  value: number;
  suffix?: string;
  highlight?: boolean;
  negative?: boolean;
}) {
  return (
    <div
      className={cn(
        'p-3 rounded-lg',
        highlight
          ? negative
            ? 'bg-red-100 dark:bg-red-900/30'
            : 'bg-green-100 dark:bg-green-900/30'
          : 'bg-neutral-100 dark:bg-neutral-800'
      )}
    >
      <p className="text-xs text-neutral-600 dark:text-neutral-400 mb-1">{label}</p>
      <p
        className={cn(
          'text-lg font-bold',
          highlight
            ? negative
              ? 'text-red-700 dark:text-red-400'
              : 'text-green-700 dark:text-green-400'
            : 'text-neutral-900 dark:text-neutral-100'
        )}
      >
        {value.toFixed(2)} {suffix}
      </p>
    </div>
  );
}

export function ResultsCard({ result }: ResultsCardProps) {
  const { t } = useTranslation();
  const isProfit = result.netProfit >= 0;

  return (
    <div className="space-y-6">
      {/* Hauptergebnis */}
      <div
        className={cn(
          'p-6 rounded-xl text-center',
          isProfit
            ? 'bg-gradient-to-br from-green-500 to-emerald-600'
            : 'bg-gradient-to-br from-red-500 to-rose-600'
        )}
      >
        <div className="flex items-center justify-center gap-2 mb-2">
          {isProfit ? (
            <TrendingUp className="h-6 w-6 text-white/80" />
          ) : (
            <TrendingDown className="h-6 w-6 text-white/80" />
          )}
          <span className="text-white/80 text-sm font-medium">{t('pricing.netProfit')}</span>
        </div>
        <p className="text-4xl font-bold text-white">{result.netProfit.toFixed(2)} €</p>
        <p className="text-white/70 text-sm mt-1">{t('pricing.perUnit')}</p>
      </div>

      {/* Preis-Aufschlüsselung */}
      <div>
        <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
          {t('pricing.prices')}
        </h4>
        <div className="grid grid-cols-3 gap-3">
          <StatBox label={t('pricing.grossPrice')} value={result.sellingPriceGross} />
          <StatBox label={t('pricing.netPrice')} value={result.sellingPriceNet} />
          <StatBox label={t('pricing.vat')} value={result.taxAmount} />
        </div>
      </div>

      {/* Kennzahlen */}
      <div>
        <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
          {t('pricing.metrics')}
        </h4>
        <div className="grid grid-cols-3 gap-3">
          <StatBox
            label={t('pricing.margin')}
            value={result.marginPercent}
            suffix="%"
            highlight
            negative={result.marginPercent < 0}
          />
          <StatBox label={t('pricing.markupLabel')} value={result.markupPercent} suffix="%" />
          <StatBox
            label={t('pricing.roi')}
            value={result.roi}
            suffix="%"
            highlight
            negative={result.roi < 0}
          />
        </div>
      </div>

      {/* Deckungsbeitrag */}
      <div>
        <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
          {t('pricing.contributionMargin')}
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <StatBox label={t('pricing.contributionMarginAbs')} value={result.contributionMargin} />
          <StatBox label={t('pricing.contributionMarginPercent')} value={result.contributionMarginPercent} suffix="%" />
        </div>
      </div>

      {/* Break-Even */}
      <div>
        <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
          {t('pricing.breakEven')}
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <StatBox label={t('pricing.minGrossPrice')} value={result.breakEvenPrice} />
          <StatBox
            label={t('pricing.unitsForRoi')}
            value={result.breakEvenUnits}
            suffix={t('pricing.unitsSuffix')}
          />
        </div>
      </div>

      {/* Warnungen */}
      {result.warnings.length > 0 && (
        <div className="space-y-2">
          {result.warnings.map((warning, i) => (
            <div
              key={i}
              className={cn(
                'flex items-center gap-2 p-3 rounded-lg text-sm',
                warning.type === 'error'
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                  : warning.type === 'warning'
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                    : 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
              )}
            >
              {warning.type === 'error' ? (
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              ) : (
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
              )}
              {warning.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
